// @ts-ignore
import StoreHandler from "../store";
import { appNavigate, currentLocation } from "../navigation";
// @ts-ignore
import { logout } from "../userActions";

// api.ts sits on an import cycle with ../store (store.js → reducers → actions → api.ts).
// When store.js enters the cycle first (index.jsx imports it before anything that pulls in
// api.ts), this module's body runs while store.js is still evaluating — a module-level
// `StoreHandler.store` read throws a TDZ ReferenceError and blanks the whole app at boot.
// Read the store lazily instead; by the time any API call runs, the cycle has resolved.
const getStore = () => StoreHandler.store;

//Let failed status codes lead to rejected promise
//https://www.tjvantoll.com/2015/09/13/fetch-and-errors/
const handleErrors = <T = any>(response: Response): Promise<T> => {
  if (!response.ok) {
    if (response.status === 401) {
      const { pathname, search } = currentLocation();
      const from = pathname + search;
      appNavigate("/?from=" + encodeURIComponent(from));
      getStore().dispatch(logout());
    }
    return response.text().then((text) => {
      try {
        return Promise.reject(JSON.parse(text));
      } catch (e) {
        return Promise.reject({ message: text });
      }
    });
  }
  return response.json().then((msg) => Promise.resolve(msg));
};

const getCommonHeaders = () => {
  const headers = new Headers();
  const state = getStore().getState();
  const params = new URLSearchParams(window.location.search);

  // redux-persist@6's typings predate redux@5's 3-arg Reducer<S, A, PreloadedState>, so TS
  // conservatively marks every combineReducers slice optional here even though userReducer's
  // default parameter guarantees `user` is always defined at runtime.
  if (state.user?.locale) {
    headers.append("Accept-Language", state.user!.locale);
  }

  const token = params.get("token");
  if (token) {
    headers.append("X-Share-Token", token);
  }
  if (window.location.pathname.startsWith("/bs")) {
    headers.append("X-Bienenschweiz", "1");
  }

  return headers;
};

const apiEndpoint =
  window.location.origin + (import.meta.env.DEV ? "/api/" : "/");

export const authenticatedGet = <T = any>(route: string) => {
  const headers = getCommonHeaders();
  return fetch(apiEndpoint + route, {
    method: "GET",
    credentials: "include",
    headers,
  }).then(handleErrors<T>);
};

export const authenticatedPost = <T = any>(route: string, body = {}) => {
  const headers = getCommonHeaders();
  headers.append("Content-Type", "application/json");
  return fetch(apiEndpoint + route, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body),
  }).then(handleErrors<T>);
};

export const authenticatedDelete = <T = any>(route: string) => {
  const headers = getCommonHeaders();
  return fetch(apiEndpoint + route, {
    method: "DELETE",
    credentials: "include",
    headers,
  }).then(handleErrors<T>);
};
